import { Claims } from '@database/types';

const MONEY_TYPES = new Set<string>([Claims.ResolutionType.REFUND, Claims.ResolutionType.STORE_CREDIT]);

type ResolveItemLike =
  | {
      resolution_final?: unknown;
      resolutionFinal?: unknown;
      resolution_percentage?: unknown;
      resolutionPercentage?: unknown;
      resolution_flat_amount?: unknown;
      resolutionFlatAmount?: unknown;
    }
  | null
  | undefined;

/** A money-typed item at 0% with no flat amount pays nothing — a shipping-only refund lists its items this way. */
function paysSomething(item: ResolveItemLike): boolean {
  const flat = Number(item?.resolution_flat_amount ?? item?.resolutionFlatAmount ?? 0);
  if (flat > 0) return true;
  const percentage = item?.resolution_percentage ?? item?.resolutionPercentage;
  return percentage == null || Number(percentage) > 0;
}

export const PRICED_INTENT_REQUIRED_MESSAGE =
  'this approval pays REFUND / STORE_CREDIT money you stated yourself, which the platform no longer accepts. ' +
  'get_resolution_pricing IS in your tool list: price these same items with it, then call resolve_claim with ' +
  'that exact intent in `intent` and omit claim_items, refund_shipping and courtesy_store_credit. Never restate ' +
  'the priced amounts.';

function itemTypes(items: unknown): string[] {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item: ResolveItemLike) => paysSomething(item))
    .map((item: ResolveItemLike) => item?.resolution_final ?? item?.resolutionFinal)
    .filter((type): type is string => typeof type === 'string');
}

/**
 * A pure-money approval the model priced itself. Every dollar promised to a customer must come from
 * the pricer, so an APPROVE whose claim items are all REFUND / STORE_CREDIT has to carry a priced
 * `intent`. Shapes the intent cannot yet express are left alone: reships (with or without money on
 * other items), lines added from the order, and claims the brand resolved outside the platform.
 */
export function legacyMoneyWithoutIntent(input: Record<string, unknown>): boolean {
  if (input.decision !== Claims.AIDecision.APPROVE || input.resolved_externally === true) return false;

  const intent = input.intent as { instrument?: unknown } | null | undefined;
  if (intent && typeof intent === 'object' && intent.instrument) return false;

  const types = itemTypes(input.claim_items);
  if (!types.some(type => MONEY_TYPES.has(type))) return false;
  if (types.includes(Claims.ResolutionType.RESHIP)) return false;

  const additional = input.additional_claim_items;
  if (Array.isArray(additional) && additional.length > 0) return false;

  return true;
}
